import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Bar,
} from 'recharts';

interface Signal {
  id: string;
  signalType: string;
  strength: number;
  detectedAt: string;
  isActive?: boolean;
}

interface SignalChartDataPoint {
  time: string;
  strength: number;
  type: string;
  isActive: number;
  timeNum: number;
}

interface SignalStrengthChartProps {
  signalChartData: SignalChartDataPoint[];
  signals: Signal[];
}

export default function SignalStrengthChart({
  signalChartData,
  signals,
}: SignalStrengthChartProps) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">RLM SIGNAL STRENGTH</h2>
          <p className="text-sm text-gray-400 mt-1">
            Reverse Line Movement signal intensity over time - Higher = stronger sharp action
          </p>
        </div>
        <div className="flex gap-2">
          {signals.length > 0 && (
            <span className={`px-2 py-1 text-xs rounded ${
              signals[0]?.strength >= 0.8 ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
              signals[0]?.strength >= 0.5 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
              'bg-gray-500/20 text-gray-400 border border-gray-500/30'
            }`}>
              Current: {((signals[0]?.strength || 0) * 100).toFixed(0)}%
            </span>
          )}
        </div>
      </div>
      <div className="h-48">
        {signalChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={signalChartData}>
              <XAxis
                dataKey="time"
                stroke="#6b7280"
                tick={{ fontSize: 11 }}
              />
              <YAxis
                stroke="#6b7280"
                tick={{ fontSize: 11 }}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                label={{ value: 'Strength', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a2e',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                }}
                formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
              />
              <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="3 3" label={{ value: 'Strong', fill: '#22c55e', fontSize: 9 }} />
              <ReferenceLine y={50} stroke="#eab308" strokeDasharray="3 3" label={{ value: 'Moderate', fill: '#eab308', fontSize: 9 }} />
              <Area
                type="monotone"
                dataKey="strength"
                name="Signal Strength"
                stroke="#a855f7"
                fill="url(#signalGradient)"
                strokeWidth={2}
              />
              <defs>
                <linearGradient id="signalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a855f7" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              {/* Show signal type as bars */}
              <Bar
                dataKey="isActive"
                name="Active Signals"
                fill="#22c55e"
                fillOpacity={0.3}
                maxBarSize={10}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="text-4xl mb-2">📡</div>
              <div>No RLM signals detected</div>
              <div className="text-xs text-gray-500 mt-1">Signals appear when sharp money is detected</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
